import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { compareWatchoutsForDisplay } from '@complan/domain';
import type { WatchoutDto } from '@complan/contracts';

import { hasCapability, useSession } from '@/app/session/session';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { findingsFromError } from '@/features/admin/admin-findings';
import { formatDate, formatDateTime } from '@/shared/format/date-format';
import { useSelectableMasterData } from '@/shared/api/selectable-master-data';

import { ResolveWatchoutDialog, WatchoutDialog } from './WatchoutDialog';
import {
  useAddWatchout,
  useEditWatchout,
  useResolveWatchout,
  useWatchouts,
} from './watchout-data';

/**
 * The Watchouts tab of the Detail_Panel (`complan-governance` R7.4, R7.8, R7.9, R7.10).
 *
 * ## The display order is the domain's, not this component's
 *
 * R7.9 requires Open first, then descending severity. That ordering is
 * `compareWatchoutsForDisplay` in `@complan/domain` - the JavaScript twin of the SQL the
 * roll-up orders by - so the tab and the R9.2 roll-up present one sequence. Sorting here
 * with a local comparator would be a second opinion about "most serious first", and the
 * two lists would eventually disagree for the same data.
 *
 * ## Write controls are gated, and their absence is not explained
 *
 * R7.4 grants add, edit and resolve to `governance:write`. Without it the tab is the list
 * alone: no disabled buttons, because a disabled control advertises an operation the
 * reader cannot perform and invites them to ask why. R7.5 makes the server reject such a
 * request anyway and audit the attempt, so this gate is convenience, never the
 * enforcement.
 *
 * ## An empty list is not "no access"
 *
 * R7.10 withholds the Watchouts of an Initiative a reader may not read, and the read
 * answers with an empty list rather than a denial - so a reader who may not see them is
 * told the same thing as a reader looking at an Initiative with no concerns. That is the
 * intended reading: the existence of a concern about a confidential record is itself
 * confidential.
 */
export function WatchoutsTab({
  initiativeId,
}: {
  readonly initiativeId: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const session = useSession();
  const canWrite = hasCapability(session.data, 'governance:write');

  const watchoutsQuery = useWatchouts(initiativeId);
  // The categories are master data (R7.3), read through the same generic selectable
  // endpoint every other reference list uses.
  const categoriesQuery = useSelectableMasterData('watchout-category');

  const addWatchout = useAddWatchout(initiativeId);
  const editWatchout = useEditWatchout(initiativeId);
  const resolveWatchout = useResolveWatchout(initiativeId);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorTarget, setEditorTarget] = useState<WatchoutDto | null>(null);
  const [resolveTarget, setResolveTarget] = useState<WatchoutDto | null>(null);

  const items = watchoutsQuery.data?.items ?? [];
  const ordered = useMemo(
    // R7.9's order, from the domain comparator the roll-up's SQL mirrors.
    () => [...items].sort(compareWatchoutsForDisplay),
    [items],
  );

  const categories = useMemo(
    () =>
      (categoriesQuery.data ?? []).map((record) => ({ id: record.id, name: record.name })),
    [categoriesQuery.data],
  );

  const closeEditor = (): void => {
    setEditorOpen(false);
    setEditorTarget(null);
    addWatchout.reset();
    editWatchout.reset();
  };

  const closeResolve = (): void => {
    setResolveTarget(null);
    resolveWatchout.reset();
  };

  return (
    <div className="flex flex-col gap-3">
      {canWrite ? (
        <div className="flex justify-end">
          <Button
            type="button"
            size="sm"
            data-testid="watchout-add"
            onClick={() => {
              addWatchout.reset();
              editWatchout.reset();
              setEditorTarget(null);
              setEditorOpen(true);
            }}
          >
            {t('watchout.add')}
          </Button>
        </div>
      ) : null}

      {watchoutsQuery.isError ? (
        <Alert variant="destructive">
          <AlertTitle>{t('common.error')}</AlertTitle>
          <AlertDescription>{t('watchout.loadError')}</AlertDescription>
        </Alert>
      ) : watchoutsQuery.isPending ? (
        <p className="text-muted-foreground text-[12.3px]">{t('common.loading')}</p>
      ) : ordered.length === 0 ? (
        <p className="text-muted-foreground text-[12.3px]">{t('watchout.empty')}</p>
      ) : (
        <Table data-testid="detail-watchouts">
          <TableHeader>
            <TableRow>
              <TableHead scope="col">{t('watchout.col.category')}</TableHead>
              <TableHead scope="col">{t('watchout.col.description')}</TableHead>
              <TableHead scope="col">{t('watchout.col.severity')}</TableHead>
              <TableHead scope="col">{t('watchout.col.owner')}</TableHead>
              <TableHead scope="col">{t('watchout.col.dueDate')}</TableHead>
              <TableHead scope="col">{t('watchout.col.state')}</TableHead>
              {canWrite ? (
                <TableHead scope="col" className="text-right">
                  <span className="sr-only">{t('watchout.col.actions')}</span>
                </TableHead>
              ) : null}
            </TableRow>
          </TableHeader>
          <TableBody>
            {ordered.map((watchout) => (
              <TableRow key={watchout.id} data-testid="watchout-row">
                <TableCell className="text-txt">
                  {watchout.watchoutCategoryName ?? watchout.watchoutCategoryId}
                </TableCell>
                <TableCell className="whitespace-normal">{watchout.description}</TableCell>
                <TableCell>
                  <Badge variant={watchout.severity === 'high' ? 'destructive' : 'outline'}>
                    {t(`watchoutSeverity.${watchout.severity}`)}
                  </Badge>
                </TableCell>
                <TableCell>
                  {watchout.ownerDisplayName ?? t('detail.value.none')}
                </TableCell>
                <TableCell className="tabular-nums">
                  {watchout.dueDate === null ? t('detail.value.none') : formatDate(watchout.dueDate)}
                </TableCell>
                <TableCell>
                  {/* R7.2: the state is the server's, derived from the resolution
                      timestamp alone. The resolution note and who closed it are shown
                      beneath it, because R7.9 asks for the note and a closed concern
                      without its reason is not much use. */}
                  <Badge variant={watchout.state === 'open' ? 'outline' : 'secondary'}>
                    {t(`watchout.state.${watchout.state}`)}
                  </Badge>
                  {watchout.resolvedAt !== null ? (
                    <span className="text-muted-foreground mt-1 block text-[11px]">
                      {t('watchout.resolvedBy', {
                        who: watchout.resolvedByDisplayName ?? t('detail.value.none'),
                        when: formatDateTime(watchout.resolvedAt),
                      })}
                      {watchout.resolutionNote === null ? null : (
                        <span className="block">{watchout.resolutionNote}</span>
                      )}
                    </span>
                  ) : null}
                </TableCell>
                {canWrite ? (
                  <TableCell className="text-right">
                    <span className="flex justify-end gap-1">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        data-testid="watchout-edit"
                        onClick={() => {
                          addWatchout.reset();
                          editWatchout.reset();
                          setEditorTarget(watchout);
                          setEditorOpen(true);
                        }}
                      >
                        {t('watchout.edit')}
                      </Button>
                      {/* R7.8: only an open concern can be resolved, and a resolved one
                          keeps its row rather than offering the action again. */}
                      {watchout.state === 'open' ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          data-testid="watchout-resolve"
                          onClick={() => {
                            resolveWatchout.reset();
                            setResolveTarget(watchout);
                          }}
                        >
                          {t('watchout.resolve')}
                        </Button>
                      ) : null}
                    </span>
                  </TableCell>
                ) : null}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <WatchoutDialog
        isOpen={editorOpen}
        watchout={editorTarget}
        categories={categories}
        isSubmitting={addWatchout.isPending || editWatchout.isPending}
        findings={
          editorTarget === null
            ? findingsFromError(addWatchout.error)
            : findingsFromError(editWatchout.error)
        }
        onConfirm={(request) => {
          if (editorTarget === null) {
            addWatchout.mutate(request, { onSuccess: closeEditor });
          } else {
            editWatchout.mutate(
              { watchoutId: editorTarget.id, request },
              { onSuccess: closeEditor },
            );
          }
        }}
        onClose={closeEditor}
      />

      <ResolveWatchoutDialog
        isOpen={resolveTarget !== null}
        isSubmitting={resolveWatchout.isPending}
        findings={findingsFromError(resolveWatchout.error)}
        onConfirm={(resolutionNote) => {
          if (resolveTarget !== null) {
            resolveWatchout.mutate(
              { watchoutId: resolveTarget.id, request: { resolutionNote } },
              { onSuccess: closeResolve },
            );
          }
        }}
        onClose={closeResolve}
      />
    </div>
  );
}
